// ═══════════════════════════════════════════════════════════════
// RollCall — Personnel Roster
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, ActionIcon, Modal, Tabs,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconEye, IconEyeOff, IconFileImport, IconId,
  IconAddressBook,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useRollCallStore } from '../../store/rollcallStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'agency_asc', label: 'Agency (A-Z)' },
  { value: 'newest', label: 'Newest First' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

type DirEmployee = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  department: string;
  employeeId: string;
  status: string;
};

export default function PersonnelList() {
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('rollcall');
  const { personnel, loadPersonnel, deletePersonnel } = useRollCallStore();
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [agencyFilter, setAgencyFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('name_asc');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const [revealedPins, setRevealedPins] = useState<Record<number, boolean>>({});
  const [deleteTarget, setDeleteTarget] = useState<typeof personnel[0] | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('directory');
  const [dirEmployees, setDirEmployees] = useState<DirEmployee[]>([]);
  const [dirLoading, setDirLoading] = useState(false);
  const [dirSearch, setDirSearch] = useState('');

  useEffect(() => { loadPersonnel(); }, []);

  useEffect(() => {
    if (!TABLES.people) return;
    setDirLoading(true);
    const FP = FIELDS.people;
    queryRecords<Record<string, unknown>>(TABLES.people, {
      filters: [
        { field: FP.type, operator: 'in', value: 'employee,staff,contractor' },
        { field: FP.status, operator: 'eq', value: 'active' },
      ],
      sort: { field: FP.lastName, direction: 'asc' },
      limit: 500,
    }).then(r => {
      setDirEmployees(r.records.map(p => ({
        id: p.record_id as number,
        firstName: String(p[FP.firstName] ?? ''),
        lastName: String(p[FP.lastName] ?? ''),
        email: String(p[FP.email] ?? ''),
        phone: String(p[FP.phone] ?? ''),
        title: String(p[FP.title] ?? ''),
        department: String(p[FP.department] ?? ''),
        employeeId: String(p[FP.employeeId] ?? ''),
        status: String(p[FP.status] ?? 'active'),
      })));
    }).catch(e => console.error('[PersonnelList] dir load', e))
      .finally(() => setDirLoading(false));
  }, []);

  // Emails of legacy rcPersonnel (for de-dup)
  const rosterEmails = useMemo(() => {
    const s = new Set<string>();
    personnel.forEach(p => { if (p.email) s.add(p.email.toLowerCase()); });
    return s;
  }, [personnel]);

  // Directory employees not already in legacy roster (by email)
  const dirFiltered = useMemo(() => {
    let list = dirEmployees;
    if (dirSearch) {
      const q = dirSearch.toLowerCase();
      list = list.filter(p =>
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.department || '').toLowerCase().includes(q) ||
        (p.title || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [dirEmployees, dirSearch]);

  const agencies = useMemo(() => {
    const set = new Set(personnel.map(p => p.agency).filter(Boolean));
    return [...set].sort().map(a => ({ value: a, label: a }));
  }, [personnel]);

  const filtered = useMemo(() => {
    let list = personnel;
    if (statusFilter !== 'all') list = list.filter(p => p.status === statusFilter);
    if (agencyFilter) list = list.filter(p => p.agency === agencyFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase().includes(q) ||
        (p.agency || '').toLowerCase().includes(q) ||
        (p.position || '').toLowerCase().includes(q) ||
        (p.pin || '').includes(q)
      );
    }
    return [...list].sort((a, b) => {
      switch (sortBy) {
        case 'name_asc': return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
        case 'name_desc': return `${b.lastName} ${b.firstName}`.localeCompare(`${a.lastName} ${a.firstName}`);
        case 'agency_asc': return (a.agency || '').localeCompare(b.agency || '');
        case 'newest': return (b.datecreated || '').localeCompare(a.datecreated || '');
        default: return 0;
      }
    });
  }, [personnel, statusFilter, agencyFilter, search, sortBy]);

  useEffect(() => { setPage(1); }, [search, statusFilter, agencyFilter, sortBy]);

  const rosterSorted = [...filtered].sort((a, b) => {
    if (!sortField) return 0;
    const va = (a as any)[sortField] ?? '';
    const vb = (b as any)[sortField] ?? '';
    const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? n : -n;
  });

  const totalPages = Math.max(1, Math.ceil(rosterSorted.length / PAGE_SIZE));
  const pageRows = rosterSorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePersonnel(deleteTarget.id);
      const displayName = (deleteTarget.firstName || deleteTarget.lastName) ? `${deleteTarget.firstName} ${deleteTarget.lastName}`.trim() : deleteTarget.fullName;
      notifications.show({ message: `${displayName} removed`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Failed to delete', color: 'var(--color-error)' });
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Personnel Roster</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconId size={14} />}
            onClick={() => navigate('/rollcall/personnel/badges')}>QR Badges</Button>
          {canEdit && <Button size="xs" variant="light" leftSection={<IconFileImport size={14} />}
            onClick={() => navigate('/rollcall/personnel/import')}>Import</Button>}
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />}
            onClick={() => navigate('/rollcall/personnel/new')}>Add Personnel</Button>}
        </Group>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab} mb="md">
        <Tabs.List>
          <Tabs.Tab value="directory" leftSection={<IconAddressBook size={14} />}>
            Directory Employees {TABLES.people ? `(${dirEmployees.length})` : ''}
          </Tabs.Tab>
          <Tabs.Tab value="roster">
            Roll Call Roster ({personnel.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="directory" pt="md">
          {!TABLES.people ? (
            <Paper p="xl" radius="sm" withBorder>
              <Text ta="center" c="dimmed">People Directory not available.</Text>
            </Paper>
          ) : (
            <>
              <div style={{ marginBottom: 16, padding: '8px 16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, fontSize: 14, color: '#0369a1' }}>
                <strong>People Directory</strong> — These are all active employees, staff, and contractors in the agency directory.
                People created in <strong>Administration → People</strong> appear here automatically.
                They can check in using the Admin check-in panel in Mobile Check-In, which links their check-in to their directory profile.
              </div>
              <Group mb="sm" gap="sm">
                <TextInput placeholder="Search name, department, email..."
                  leftSection={<IconSearch size={14} />}
                  value={dirSearch} onChange={e => setDirSearch(e.target.value)}
                  size="xs" style={{ flex: 1, minWidth: 200 }} />
                <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
                  onClick={() => navigate('/admin/people?type=employee&returnTo=/rollcall/personnel')}>
                  Add to Directory
                </Button>
              </Group>
              {dirLoading ? (
                <Text size="sm" c="dimmed" ta="center" py="lg">Loading directory...</Text>
              ) : dirFiltered.length === 0 ? (
                <Paper p="xl" radius="sm" withBorder>
                  <Text ta="center" c="dimmed">
                    {dirEmployees.length === 0
                      ? 'No active employees in the directory. Add employees in Administration → People.'
                      : 'No results match your search.'}
                  </Text>
                </Paper>
              ) : (
                <Paper p={0} radius="sm" withBorder>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                          <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Name</th>
                          <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Department</th>
                          <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Title</th>
                          <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Email</th>
                          <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Roster Status</th>
                          <th style={{ padding: '8px 16px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {dirFiltered.map(dp => {
                          const inRoster = dp.email ? rosterEmails.has(dp.email.toLowerCase()) : false;
                          return (
                            <tr key={dp.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                              <td style={{ padding: '8px 16px', fontWeight: 600 }}>
                                {`${dp.firstName} ${dp.lastName}`.trim() || '—'}
                              </td>
                              <td style={{ padding: '8px 16px' }}>{dp.department || '—'}</td>
                              <td style={{ padding: '8px 16px' }}>{dp.title || '—'}</td>
                              <td style={{ padding: '8px 16px', fontSize: 12 }}>{dp.email || '—'}</td>
                              <td style={{ padding: '8px 16px' }}>
                                {inRoster
                                  ? <Badge color="var(--color-success)" size="xs">On Roster</Badge>
                                  : <Badge color="#939598" size="xs" variant="outline">Not on Roster</Badge>}
                              </td>
                              <td style={{ padding: '8px 16px' }}>
                                <Group gap={4}>
                                  <ActionIcon size="sm" variant="subtle" color="var(--color-primary-700)"
                                    title="View in directory"
                                    onClick={() => navigate(`/admin/people/${dp.id}`)}>
                                    <IconAddressBook size={14} />
                                  </ActionIcon>
                                  {!inRoster && canEdit && (
                                    <Button size="xs" variant="light" color="var(--color-success)"
                                      onClick={() => navigate(`/rollcall/personnel/new?prefill=${encodeURIComponent(JSON.stringify({ firstName: dp.firstName, lastName: dp.lastName, email: dp.email, phone: dp.phone, position: dp.title, agency: dp.department }))}`)}>
                                      Enroll
                                    </Button>
                                  )}
                                </Group>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Paper>
              )}
            </>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="roster" pt="md">
          <Group mb="md" gap="sm">
            <TextInput placeholder="Search name, agency, PIN..." leftSection={<IconSearch size={14} />}
              value={search} onChange={e => setSearch(e.target.value)} size="xs" style={{ flex: 1, minWidth: 200 }} />
            <Select data={STATUS_OPTIONS} value={statusFilter}
              onChange={v => setStatusFilter(v ?? 'all')} size="xs" w={120} />
            <Select data={agencies} value={agencyFilter} onChange={setAgencyFilter}
              placeholder="Agency" clearable searchable size="xs" w={180} />
            <Select data={SORT_OPTIONS} value={sortBy} onChange={v => setSortBy(v ?? 'name_asc')}
              size="xs" w={160} />
          </Group>

          <Text size="xs" c="dimmed" mb="sm">Showing {rosterSorted.length} of {personnel.length}</Text>

          {rosterSorted.length === 0 ? (
            <Paper p="xl" radius="sm" withBorder>
              <Text ta="center" c="dimmed">No personnel found.</Text>
            </Paper>
          ) : (
            <Paper p={0} radius="sm" withBorder>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                      {[
                        { label: 'Name', field: 'lastName' },
                        { label: 'Agency', field: 'agency' },
                        { label: 'Position', field: 'position' },
                        { label: 'PIN', field: '' },
                        { label: 'Status', field: 'status' },
                        { label: '', field: '' },
                      ].map(col => (
                        <th
                          key={col.label}
                          onClick={() => col.field && toggleSort(col.field)}
                          style={{
                            textAlign: 'left',
                            padding: '8px 16px',
                            fontWeight: 600,
                            color: sortField === col.field && col.field ? '#4B3FFF' : undefined,
                            whiteSpace: 'nowrap',
                            cursor: col.field ? 'pointer' : 'default',
                            userSelect: 'none',
                          }}>
                          {col.label}
                          {col.field && (
                            <span style={{ marginLeft: 4, opacity: sortField === col.field ? 1 : 0.25 }}>
                              {sortField === col.field && sortDir === 'desc' ? '↓' : '↑'}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(p => (
                      <tr key={p.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                        <td style={{ padding: '8px 16px', fontWeight: 600 }}>
                          {(p.firstName || p.lastName) ? `${p.firstName} ${p.lastName}`.trim() : (p.fullName || '—')}
                        </td>
                        <td style={{ padding: '8px 16px' }}>{p.agency || '—'}</td>
                        <td style={{ padding: '8px 16px', textTransform: 'capitalize' }}>
                          {(p.position || '—').replace(/_/g, ' ')}
                        </td>
                        <td style={{ padding: '8px 16px' }}>
                          <Group gap={4}>
                            <Text size="xs" ff="monospace">{revealedPins[p.id] ? p.pin : '••••'}</Text>
                            <ActionIcon size="xs" variant="subtle"
                              onClick={() => setRevealedPins(prev => ({ ...prev, [p.id]: !prev[p.id] }))}>
                              {revealedPins[p.id] ? <IconEyeOff size={12} /> : <IconEye size={12} />}
                            </ActionIcon>
                          </Group>
                        </td>
                        <td style={{ padding: '8px 16px' }}>
                          <Badge color={p.status === 'active' ? 'var(--color-success)' : '#939598'} size="xs">{p.status}</Badge>
                        </td>
                        <td style={{ padding: '8px 16px' }}>
                          <Group gap={4}>
                            <ActionIcon size="sm" variant="subtle" color="var(--color-primary-700)"
                              title="Generate badge"
                              onClick={() => navigate(`/rollcall/personnel/badges?id=${p.id}`)}>
                              <IconId size={14} />
                            </ActionIcon>
                            {canEdit && <ActionIcon size="sm" variant="subtle"
                              onClick={() => navigate(`/rollcall/personnel/${p.id}/edit`)}>
                              <IconPencil size={14} />
                            </ActionIcon>}
                            {canEdit && <ActionIcon size="sm" variant="subtle" color="var(--color-error)"
                              onClick={() => setDeleteTarget(p)}>
                              <IconTrash size={14} />
                            </ActionIcon>}
                          </Group>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Paper>
          )}

          {totalPages > 1 && (
            <Group justify="center" mt="sm" gap="xs">
              <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
              <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
                return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="var(--color-primary-700)" size="xs" onClick={() => setPage(p)}>{p}</Button>;
              })}
              <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
              <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
            </Group>
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Delete Confirm */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Remove Personnel" centered size="sm">
        <Text size="sm" mb="md">
          Remove {deleteTarget?.firstName} {deleteTarget?.lastName} from the roster?
        </Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="var(--color-error)" onClick={handleDelete}>Remove</Button>
        </Group>
      </Modal>
    </div>
  );
}
